/**
 * Variant Forge — derive a sibling variant from a template variant you already like.
 *
 * The normal "AI Email Text" flow writes from a blank page: it only sees the
 * existing variants as a "don't repeat this" list. That is the wrong tool when
 * one variant is already performing and you want three more just like it.
 *
 * So this builds a prompt around ONE source variant and two knobs the user
 * controls separately:
 *
 *   similarity   how far the PROSE may travel from the original
 *   bracePolicy  how far the {single brace AI instructions} may travel
 *
 * Those are deliberately independent. The instructions inside braces are the
 * expensive part of a template — they encode which data source to read, what to
 * output, the length limit and a worked example. A user who spent an hour
 * tuning them wants new prose around instructions that stay byte-identical, and
 * that combination (cousin prose + verbatim braces) is impossible to express
 * with a single "creativity" slider.
 *
 * There is no refinement pass here, unlike fresh generation. The source variant
 * has already been polished, so it acts as the syntax reference — and the polish
 * prompt is explicitly told to reword vague instructions, which would quietly
 * break a `verbatim` promise. `auditDerivedVariant` verifies the result instead.
 */

import { extractInstructionBlocks } from './templateBraceCheck';

/** Derived variants are one full writer call each, so the ceiling stays low. */
export const MAX_FORGE_VARIANTS = 3;

export const SIMILARITY_LEVELS = ['twin', 'sibling', 'cousin'];
export const BRACE_POLICIES = ['verbatim', 'adapt', 'rethink'];
export const LENGTH_MODES = ['match', 'shorter', 'longer'];

export const DEFAULT_FORGE_CONFIG = {
  similarity: 'sibling',
  bracePolicy: 'verbatim',
  lengthMode: 'match',
  count: 1,
  keepFormatting: true,
  note: '',
};

/** How much of the original's word count each length mode aims for. */
const LENGTH_FACTORS = { match: 1, shorter: 0.65, longer: 1.4 };

const SIMILARITY_DIRECTIVES = {
  twin: `NEAR-IDENTICAL TWIN.
Keep the same structure, the same beats in the same order, the same arguments and the same length. Vary only the surface: swap in synonyms, open sentences differently, change the rhythm. Someone reading both should recognise the same email said slightly differently.
Do NOT add an argument the original does not make. Do NOT drop one it does make.`,
  sibling: `SAME SKELETON, FRESH PHRASING.
Keep the beat order and every argument of the original, but rewrite each beat in genuinely new words — not a synonym swap. Sentence lengths and rhythm may differ. The strategy is fixed; the prose is new.
A reader should feel this was written by the same person on a different day, not generated from a thesaurus.`,
  cousin: `SAME GOAL, DIFFERENT ROUTE.
Keep the core message, the offer and the intent of the call-to-action, but you may restructure freely: find a new angle for the hook, reorder the beats, frame the same value differently.
It should read as a distinct email from the same campaign — a genuine alternative, not a paraphrase. Do not change what is being offered or who it is for.`,
};

const BRACE_DIRECTIVES = {
  verbatim: `COPY THE AI INSTRUCTIONS CHARACTER-FOR-CHARACTER.
Every {single brace instruction} from the original must appear in your variant with EXACTLY the same text inside the braces — same wording, same examples, same word limits, same data-source references. Copy them; do not retype them from memory.
You may move an instruction to a different position if your structure changed. If a beat genuinely no longer exists you may leave its instruction out. You may NOT reword one, and you may NOT invent new ones.
What you vary is the plain text AROUND the instructions.`,
  adapt: `KEEP EACH AI INSTRUCTION'S JOB, REPHRASE ITS WORDING.
Every {single brace instruction} must keep pointing at the SAME data source and producing the SAME kind of output. Within that, you may rephrase the instruction, adjust its word limit, or swap its example for a different example of the same shape.
Never change WHAT an instruction looks up or WHAT it returns — only how it is phrased.
Every instruction you write must still state all four: (a) which data source to read, (b) exactly what to output, (c) a max word or character limit, (d) an example of good output.`,
  rethink: `REDESIGN THE AI INSTRUCTIONS.
You may point instructions at different data sources, ask for different output, add an instruction where the original had static text, or turn an instruction into static text where it reads better fixed.
The template must stay at least as dynamic as the original — do not end up with fewer instructions than it had.
Every instruction you write must state all four: (a) which data source to read, (b) exactly what to output, (c) a max word or character limit, (d) an example of good output.`,
};

/**
 * Words a human sees in the editor — brace content excluded.
 *
 * Matches the word counter on the variant card, so a length instruction the
 * model receives means the same thing as the number the user was looking at
 * when they picked "shorter".
 *
 * @param {string} text template body
 * @returns {number}
 */
export function countTemplateWords(text) {
  if (!text) return 0;
  return text
    .replace(/\{\{[^{}]+\}\}/g, '')
    .replace(/\{[^{}]+\}/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .length;
}

/** How many {single brace AI instructions} the text carries. */
export function countTemplateInstructions(text) {
  return extractInstructionBlocks(text).length;
}

/** Distinct {{variable}} names in the text, image tokens excluded. */
export function collectVariableNames(text) {
  const names = new Set();
  if (!text) return names;
  const regex = /\{\{\s*([^{}]*?)\s*\}\}/g;
  let match = regex.exec(text);
  while (match) {
    const key = match[1].trim();
    if (key && !key.startsWith('image:')) names.add(key);
    match = regex.exec(text);
  }
  return names;
}

/** Whitespace- and case-insensitive form, for comparing two blocks of copy. */
const normalize = (text) => (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

const clamp = (n, min, max) => Math.min(Math.max(Number(n) || min, min), max);

/** Fill in whatever the caller left out, and clamp what it got wrong. */
export function normalizeForgeConfig(config = {}) {
  const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
  return {
    similarity: pick(config.similarity, SIMILARITY_LEVELS, DEFAULT_FORGE_CONFIG.similarity),
    bracePolicy: pick(config.bracePolicy, BRACE_POLICIES, DEFAULT_FORGE_CONFIG.bracePolicy),
    lengthMode: pick(config.lengthMode, LENGTH_MODES, DEFAULT_FORGE_CONFIG.lengthMode),
    count: clamp(config.count, 1, MAX_FORGE_VARIANTS),
    keepFormatting: config.keepFormatting !== false,
    note: typeof config.note === 'string' ? config.note.trim() : '',
  };
}

/**
 * Build the writer prompt for one derived variant.
 *
 * @param {Object} params
 * @param {string} params.sourceText the variant being derived from
 * @param {Object} params.config see DEFAULT_FORGE_CONFIG
 * @param {string} [params.language] output language, e.g. 'German'
 * @param {string} [params.templateName]
 * @param {string} [params.templateType] e.g. 'intro', 'follow_up_1'
 * @param {string[]} [params.avoidTexts] variants that already exist, including
 *   the ones generated earlier in this same run
 * @param {string} [params.generalInfo] the template's General Info field
 * @param {boolean} [params.smartGreeting] whether {{greeting}} resolves
 * @returns {{systemPrompt: string, userPrompt: string}}
 */
export function buildVariantForgePrompt({
  sourceText,
  config,
  language = 'German',
  templateName = '',
  templateType = '',
  avoidTexts = [],
  generalInfo = '',
  smartGreeting = false,
}) {
  const cfg = normalizeForgeConfig(config);
  const sourceWords = countTemplateWords(sourceText);
  const target = Math.round(sourceWords * LENGTH_FACTORS[cfg.lengthMode]);

  const lengthRule = {
    match: `Match the original's length: stay within roughly 15% of its ${sourceWords} words.`,
    shorter: `Make it noticeably tighter — the original has ${sourceWords} words, aim for about ${target}. Cut whole thoughts rather than compressing everything into run-on sentences.`,
    longer: `Give it more room — the original has ${sourceWords} words, aim for about ${target}. Add depth to the beats that already exist instead of bolting on new arguments.`,
  }[cfg.lengthMode];

  const formattingRule = cfg.keepFormatting
    ? "Mirror the original's formatting: the same paragraph rhythm, the same use of line breaks and blank lines, the same overall shape on screen."
    : 'You may restructure paragraphs and line breaks however reads best.';

  const instructionBlocks = extractInstructionBlocks(sourceText);
  const braceInventory = instructionBlocks.length
    ? `\n=== AI INSTRUCTIONS IN THE ORIGINAL (${instructionBlocks.length}) ===\n${instructionBlocks.map((b, i) => `${i + 1}. ${b}`).join('\n')}\n`
    : '';

  const variableNames = [...collectVariableNames(sourceText)];
  const variableRule = variableNames.length
    ? `Use exactly these {{double brace}} variables, spelled exactly as shown: ${variableNames.map(v => `{{${v}}}`).join(', ')}. Do not invent any other {{variable}} — anything outside this list is deleted at send time and leaves a gap in the sentence.`
    : 'The original uses no {{double brace}} variables. Do not introduce any.';

  const noteBlock = cfg.note
    ? `\n=== WHAT THE USER WANTS DIFFERENT (HIGH PRIORITY) ===\nThis overrides the similarity level wherever the two conflict:\n${cfg.note}\n`
    : '';

  const greetingRule = smartGreeting
    ? '- {{greeting}} is resolved per lead by the system. Keep it where the original has it and do not write your own salutation on top of it.'
    : '';

  const generalInfoRule = generalInfo
    ? `- GENERAL TEMPLATE CONTEXT (stays true for every variant): ${generalInfo}`
    : '';

  const avoid = avoidTexts.map(t => (t || '').trim()).filter(Boolean);
  const avoidBlock = avoid.length
    ? `\n=== SIBLING VARIANTS THAT ALREADY EXIST ===\nYour variant must be clearly distinct from each of these as well as from the original:\n---\n${avoid.join('\n---\n')}\n---\n`
    : '';

  const systemPrompt = `You are an expert cold email template architect. Your job right now is NOT to invent a template — it is to derive a new variant from one the user already likes and wants more of.

This is a TEMPLATE, not a finished email. It has two placeholder layers and they behave completely differently:

- {{DOUBLE BRACES}} = static variables, replaced with real lead data at send time.
- {SINGLE BRACES} = AI instructions, resolved per lead by a downstream AI that can see that lead's web research, custom fields, product data and context notes. The text inside single braces is NEVER shown to the recipient — it tells that AI what to produce at that spot.

=== HOW CLOSE THE PROSE SHOULD STAY (${cfg.similarity}) ===
${SIMILARITY_DIRECTIVES[cfg.similarity]}

=== HOW TO TREAT THE {AI INSTRUCTIONS} (${cfg.bracePolicy}) ===
${BRACE_DIRECTIVES[cfg.bracePolicy]}

=== HARD RULES ===
- ${lengthRule}
- ${formattingRule}
- ${variableRule}
${greetingRule}
${generalInfoRule}
- Never wrap ordinary email text in {single braces}. A finished sentence that goes to the recipient as-is is plain text, even when it sounds conditional ("If that's interesting, ...").
- Never nest braces inside braces.
- Do NOT write a subject line.
- Do NOT write a signature, sign-off name or closing like "Best regards" / "Thanks, {{sender_name}}" — the system appends the signature. End on the last content sentence or the CTA.
- Return ONLY the template text. No preamble, no explanation, no markdown fences, no "Variant 2:" label.

=== LANGUAGE REQUIREMENT (MANDATORY) ===
Write every word of the output in ${language}, including the text inside {single brace instructions}. Do not mix languages.`;

  const userPrompt = `${templateName ? `Template: "${templateName}"\n` : ''}${templateType ? `Type: ${templateType}\n` : ''}
=== ORIGINAL VARIANT TO DERIVE FROM ===
---
${sourceText}
---
${braceInventory}${avoidBlock}${noteBlock}
Produce exactly 1 new variant of this template, following the similarity level and the AI-instruction policy above.

REMINDER: output the template text only, entirely in ${language}.`;

  return { systemPrompt, userPrompt };
}

/**
 * Check a derived variant against its source.
 *
 * The model is asked to hold two promises the generic brace linter cannot see:
 * that `verbatim` instructions came through untouched, and that the result is
 * actually a different email. Both fail quietly — a reworded instruction still
 * lints clean, and a near-copy still looks like a valid template — so they are
 * checked here and surfaced next to the generation result.
 *
 * Issues use the same {level, code, detail, fix} shape as checkTemplateBraces
 * so both lists render through the same UI.
 *
 * @param {string} sourceText the variant it was derived from
 * @param {string} derivedText what the model returned
 * @param {Object} [options]
 * @param {string} [options.bracePolicy]
 * @param {string} [options.lengthMode]
 * @returns {{issues: Array<{level: string, code: string, detail: string, fix?: string}>}}
 */
export function auditDerivedVariant(sourceText, derivedText, options = {}) {
  const { bracePolicy = 'verbatim', lengthMode = 'match' } = options;
  const issues = [];

  if (!derivedText || !derivedText.trim()) {
    return { issues: [{ level: 'error', code: 'empty-variant', detail: 'The AI returned an empty variant.' }] };
  }

  if (normalize(sourceText) === normalize(derivedText)) {
    issues.push({
      level: 'error',
      code: 'identical-variant',
      detail: 'The generated variant is identical to the original apart from whitespace.',
      fix: 'Delete it and retry one step looser — a twin at verbatim leaves the model almost no room to move.',
    });
  }

  const sourceBlocks = extractInstructionBlocks(sourceText);
  const derivedBlocks = extractInstructionBlocks(derivedText);

  if (bracePolicy === 'verbatim' && sourceBlocks.length > 0) {
    const sourceSet = new Set(sourceBlocks.map(normalize));
    const reworded = derivedBlocks.filter(block => !sourceSet.has(normalize(block)));
    if (reworded.length > 0) {
      issues.push({
        level: 'warning',
        code: 'brace-reworded',
        detail: `${reworded.length} AI instruction${reworded.length > 1 ? 's were' : ' was'} reworded despite "keep 1:1": ${reworded[0].slice(0, 90)}${reworded[0].length > 90 ? '…' : ''}`,
        fix: 'Copy the wording back from the original variant, or accept it if the new phrasing still names the same data source, output and limit.',
      });
    }
  }

  if (sourceBlocks.length > 0 && derivedBlocks.length === 0) {
    issues.push({
      level: 'error',
      code: 'instructions-lost',
      detail: `The original has ${sourceBlocks.length} AI instruction${sourceBlocks.length > 1 ? 's' : ''}, the variant has none — it will read identically for every lead.`,
      fix: 'Discard this variant and generate again.',
    });
  } else if (derivedBlocks.length < sourceBlocks.length) {
    const missing = sourceBlocks.length - derivedBlocks.length;
    issues.push({
      level: 'warning',
      code: 'fewer-instructions',
      detail: `${missing} fewer AI instruction${missing > 1 ? 's' : ''} than the original (${derivedBlocks.length} vs ${sourceBlocks.length}) — this variant personalizes less.`,
      fix: 'Check which beat lost its instruction and paste it back if you want it.',
    });
  }

  const sourceVars = collectVariableNames(sourceText);
  const invented = [...collectVariableNames(derivedText)].filter(v => !sourceVars.has(v));
  if (invented.length > 0) {
    issues.push({
      level: 'warning',
      code: 'invented-variable',
      detail: `New variable${invented.length > 1 ? 's' : ''} the original does not use: ${invented.map(v => `{{${v}}}`).join(', ')}.`,
      fix: 'Anything the send-time body pipeline cannot resolve is stripped to an empty string. Replace it with plain text or a known variable.',
    });
  }

  if (lengthMode === 'match') {
    const sourceWords = countTemplateWords(sourceText);
    const derivedWords = countTemplateWords(derivedText);
    if (sourceWords >= 20 && Math.abs(derivedWords - sourceWords) / sourceWords > 0.4) {
      issues.push({
        level: 'warning',
        code: 'length-drift',
        detail: `${derivedWords} words vs the original's ${sourceWords}, despite "keep the length".`,
        fix: derivedWords > sourceWords ? 'Trim it back if length matters for this step.' : 'Check that no beat got dropped along the way.',
      });
    }
  }

  return { issues };
}

export default buildVariantForgePrompt;
